/**
 * Coerce an API decimal to a number for arithmetic.
 *
 * Decimal/money fields are serialized as strings to preserve precision
 * (ActiveSupport encodes BigDecimal as a string). JS `*` and `-` coerce strings
 * implicitly, but `+` does not — `"1.0" + 1 === "1.01"` — so every arithmetic
 * use of an API decimal must go through `num()` first.
 *
 * Returns 0 for null/undefined/unparseable input.
 */
export const num = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') return value
  if (value == null) return 0
  const parsed = parseFloat(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * Serialize a number to a decimal string for an API request body. Decimal
 * fields are strings on the wire in both directions (see `num` for reads).
 *
 * These are TypeScript overloads — two signature lines plus one implementation,
 * a single callable function (not three). A required `number` returns a
 * guaranteed `string`; a nullable input returns `string | undefined` so
 * optional / nullable fields stay omittable from the payload.
 */
export function toDecimal(value: number): string
export function toDecimal(value: number | null | undefined): string | undefined
export function toDecimal(value: number | null | undefined): string | undefined {
  return value == null ? undefined : String(value)
}

/**
 * Like `toDecimal`, but null PASSES THROUGH instead of being dropped — for
 * clearable decimals (line_items.unit_price) where the wire must distinguish
 * "clear this price" (null) from "leave it untouched" (key absent/undefined).
 * `toDecimal` cannot serve that write: it folds null into undefined, and an
 * omitted key means the old price silently survives.
 */
export function toDecimalOrNull(value: number | null): string | null
export function toDecimalOrNull(value: number | null | undefined): string | null | undefined
export function toDecimalOrNull(value: number | null | undefined): string | null | undefined {
  if (value === undefined) return undefined
  return value === null ? null : String(value)
}

/**
 * Read a `<input type="number">` back as a number, or `null` for « not
 * answered ». A cleared numeric input hands back `''`, and folding that to 0
 * is the bug this exists to prevent: 0 is a price, an empty field is a
 * question nobody answered — the gates that block on a missing amount read
 * null, not falsy.
 *
 * ⚠ `Number`, not `parseFloat`: `parseFloat('12abc')` is 12, which is how junk
 * becomes a total. Anything unparseable is unanswered.
 */
export const numberOrNull = (value: string | number): number | null => {
  const raw = String(value).trim()
  if (raw === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}
